// src/pages/Plans.tsx
import { Link } from "react-router-dom";
import type { PlanConfig, PlanKey } from "../config/plans";
import { PLANS, PLAN_ORDER } from "../config/plans";
import { useI18n } from "../i18n/i18n";

function formatUSD(n: number) {
  // 0 -> "0", 9 -> "9", 19 -> "19"
  return String(Math.round(n));
}

export default function Plans() {
  const { t } = useI18n();

  // ✅ convierte Record -> array ordenado (y con typing fuerte)
  const plansList: PlanConfig[] = PLAN_ORDER.map((k: PlanKey) => PLANS[k]);

  const planDescById: Record<PlanKey, string> = {
    free: t("plans.planDesc.free"),
    basic: t("plans.planDesc.basic"),
    pro: t("plans.planDesc.pro"),
    premium: t("plans.planDesc.premium"),
  };

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white px-6 py-20">
      <div className="max-w-6xl mx-auto">
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-semibold">{t("plans.title")}</h1>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto">{t("plans.subtitle")}</p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/signup"
              className="min-w-[190px] px-6 py-3 bg-white text-black rounded-xl text-sm font-semibold hover:bg-gray-200 transition text-center"
            >
              {t("plans.ctaSignup")}
            </Link>
            <Link
              to="/login"
              className="min-w-[190px] px-6 py-3 border border-white/15 rounded-xl text-sm font-semibold hover:bg-white/10 transition text-center"
            >
              {t("plans.ctaLogin")}
            </Link>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          {plansList.map((plan: PlanConfig) => (
            <div
              key={plan.id}
              className={[
                "rounded-2xl border p-6 text-left overflow-hidden relative",
                plan.id === "pro"
                  ? "border-indigo-400/35 bg-indigo-500/10"
                  : "border-white/10 bg-white/5",
              ].join(" ")}
            >
              <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl" />

              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  <p className="mt-1 text-sm text-gray-400">{planDescById[plan.id]}</p>
                </div>

                {plan.id === "pro" && (
                  <span className="shrink-0 rounded-xl border border-indigo-400/30 bg-indigo-500/10 px-2.5 py-1 text-xs text-indigo-200">
                    {t("plans.recommended")}
                  </span>
                )}
              </div>

              <div className="mt-5">
                <div className="text-3xl font-bold">
                  {plan.priceMonthlyUSD === 0 ? (
                    t("plans.freePrice")
                  ) : (
                    <>
                      ${formatUSD(plan.priceMonthlyUSD)}
                      <span className="text-sm text-gray-400 font-normal">{t("plans.perMonth")}</span>
                    </>
                  )}
                </div>

                <div className="mt-2 text-sm text-gray-400">
                  {plan.creditsMonthly} {t("plans.creditsPerMonth")}
                </div>
              </div>

              <div className="mt-5 space-y-2 text-sm text-gray-300">
                <div className="flex gap-2">
                  <span className="text-indigo-300">•</span>
                  {t("plans.features.resolutions")}{" "}
                  <span className="text-gray-200">{plan.allowedResolutions.join(" · ").toUpperCase()}</span>
                </div>

                <div className="flex gap-2">
                  <span className="text-indigo-300">•</span>
                  {t("plans.features.brandKit")}{" "}
                  <span className="text-gray-200">
                    {plan.brand.canPersistBrandKit ? t("plans.values.persistent") : t("plans.values.demoNoSave")}
                  </span>
                </div>

                <div className="flex gap-2">
                  <span className="text-indigo-300">•</span>
                  {t("plans.features.useBrandInGen")}{" "}
                  <span className="text-gray-200">
                    {plan.brand.canUseBrandInGeneration ? t("plans.values.yes") : t("plans.values.no")}
                  </span>
                </div>

                <div className="flex gap-2">
                  <span className="text-indigo-300">•</span>
                  {t("plans.features.advancedFields")}{" "}
                  <span className="text-gray-200">
                    {plan.brand.canUseAdvancedBrandFields ? t("plans.values.yes") : t("plans.values.no")}
                  </span>
                </div>

                <div className="flex gap-2">
                  <span className="text-indigo-300">•</span>
                  {t("plans.features.activeKits")}{" "}
                  <span className="text-gray-200">
                    {plan.brand.brandKitsLimit >= 999999 ? t("plans.values.unlimited") : plan.brand.brandKitsLimit}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/signup"
                  className={[
                    "inline-flex w-full justify-center rounded-xl py-2.5 text-sm font-semibold transition",
                    plan.id === "pro"
                      ? "bg-white text-black hover:bg-gray-200"
                      : "border border-white/15 hover:bg-white/10",
                  ].join(" ")}
                >
                  {plan.id === "free" ? t("plans.btnStartFree") : t("plans.btnChoosePlan")}
                </Link>

                <Link
                  to="/upgrade"
                  className="mt-3 inline-flex w-full justify-center rounded-xl py-2.5 text-xs font-semibold border border-white/10 bg-black/20 hover:bg-white/5 transition text-gray-200"
                >
                  {t("plans.haveAccountUpgrade")}
                </Link>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-gray-500 text-center">{t("plans.disclaimer")}</p>
      </div>
    </div>
  );
}
